import type { Doc } from '@blocksuite/store';
import { materializePendingMindmapForPage } from '@pique/core/components/blocksuite/block-suite-mode-switch/pending-mindmap';
import { useAugmentationView } from '@pique/core/components/blocksuite/block-suite-mode-switch/augmentation-view-context';
import { useEnableCloud } from '@pique/core/hooks/pique/use-enable-cloud';
import { useActiveBlocksuiteEditor } from '@pique/core/hooks/use-block-suite-editor';
import { WorkspacePropertiesAdapter } from '@pique/core/modules/properties';
import { type Workspace, useService } from '@toeverything/infra';
import { memo, useCallback } from 'react';

import { ShareMenu } from './share-menu';

type SharePageModalProps = {
  workspace: Workspace;
  page: Doc;
  compact?: boolean;
};

export const SharePageButton = memo(function SharePageButton({
  workspace,
  page,
  compact,
}: SharePageModalProps) {
  const confirmEnableCloud = useEnableCloud();
  const augmentationView = useAugmentationView();
  const [editorContainer] = useActiveBlocksuiteEditor();
  const workspacePropertiesAdapter = useService(WorkspacePropertiesAdapter);
  const sharePageId = augmentationView?.sharePageId ?? page.id;
  const handleOpenShareModal = useCallback(
    (open: boolean) => {
      const host = editorContainer?.host;
      if (!open || !host) {
        return;
      }

      materializePendingMindmapForPage({
        host,
        sourcePageId: augmentationView?.sourcePageId ?? page.id,
        workspacePropertiesAdapter,
      });
    },
    [
      augmentationView?.sourcePageId,
      editorContainer?.host,
      page.id,
      workspacePropertiesAdapter,
    ]
  );

  return (
    <ShareMenu
      workspaceMetadata={workspace.meta}
      currentPage={page}
      compact={compact}
      sharePageId={sharePageId}
      onEnableAffineCloud={() =>
        confirmEnableCloud(workspace, {
          openPageId: sharePageId,
        })
      }
      onOpenShareModal={handleOpenShareModal}
    />
  );
});
